import { OrderAction, OrderStatus } from "@prisma/client";
import { z } from "zod";

import { effectLocationTokenSchema } from "~/shared/types/EffectLocation";

import {
  OrderActionSubmitSchema,
  type OrderActionSubmitSchemaType,
  OrderActivityMarkDeliveredSchema,
  type OrderActivityMarkDeliveredSchemaType,
  OrderActivityShipInputSchema,
  OrderActivityShipSchema,
  type OrderActivityShipSchemaType,
  orderShipmentPlanSchema,
} from "./schema";

export const orderApprovalActionSchema = z.union([
  z.literal(OrderAction.SUBMIT),
  z.literal(OrderAction.SHIP),
  z.literal(OrderAction.MARK_DELIVERED),
]);
export type OrderApprovalAction = z.infer<typeof orderApprovalActionSchema>;

/** Persist the owner's instant, including milliseconds, without interpreting a picker a second time. */
const instantSchema = z
  .string()
  .datetime()
  .refine(value => {
    const date = new Date(value);
    return Number.isFinite(date.getTime()) && date.toISOString() === value;
  }, "Expected a canonical ISO instant.");

const submitItemSchema = OrderActionSubmitSchema.shape.items.element;
const submitLotSchema = submitItemSchema.shape.lots.element;
const decimalStringSchema = z.string().refine(value => {
  const parsed = submitLotSchema.shape.quantity.safeParse(value);
  return parsed.success && parsed.data.isFinite();
}, "Expected a finite positive decimal quantity.");

const persistedSubmitInputSchema = OrderActionSubmitSchema.extend({
  action: z.literal(OrderAction.SUBMIT),
  items: z
    .array(submitItemSchema.extend({ lots: z.array(submitLotSchema.extend({ quantity: decimalStringSchema }).strict()) }).strict())
    .min(1),
}).strict();

const platformSchema = orderShipmentPlanSchema.shape.shipmentPlan.shape.platforms.innerType().element;
const persistedShipmentPlanSchema = orderShipmentPlanSchema.shape.shipmentPlan
  .extend({
    platforms: z.array(platformSchema.extend({ items: z.array(platformSchema.shape.items.element.strict()) }).strict()),
  })
  .strict();

const persistedShipInputSchema = OrderActivityShipInputSchema.extend({
  action: z.literal(OrderAction.SHIP),
  roOrderedAt: instantSchema,
  shipBy: instantSchema.nullable(),
  requiredBy: instantSchema.nullable(),
  actualShipDate: instantSchema,
  poReceivedAt: instantSchema.nullable().optional(),
  // These values are materialized by the owner parse at capture time. A damaged payload must not
  // recover them from a fresh default or preprocess during execution.
  useCustomShippingInstructions: z.boolean(),
  grossShipmentWeightOverride: z.number().finite().positive().nullable(),
  palletCountOverride: z.number().int().min(0).nullable(),
  shipmentPlan: persistedShipmentPlanSchema,
}).strict();

const persistedDeliveryInputSchema = OrderActivityMarkDeliveredSchema.innerType()
  .extend({
    action: z.literal(OrderAction.MARK_DELIVERED),
    roOrderedAt: instantSchema,
    requiredBy: instantSchema.nullable(),
    deliveredOn: instantSchema,
  })
  .strict();

const payloadSchema = z.discriminatedUnion("action", [
  z.object({ version: z.literal(1), action: z.literal(OrderAction.SUBMIT), input: persistedSubmitInputSchema }).strict(),
  z.object({ version: z.literal(1), action: z.literal(OrderAction.SHIP), input: persistedShipInputSchema }).strict(),
  z.object({ version: z.literal(1), action: z.literal(OrderAction.MARK_DELIVERED), input: persistedDeliveryInputSchema }).strict(),
]);

export type OrderApprovalActionPayloadV1 = z.infer<typeof payloadSchema>;
export type DecodedOrderApprovalAction =
  | { action: typeof OrderAction.SUBMIT; input: OrderActionSubmitSchemaType }
  | { action: typeof OrderAction.SHIP; input: OrderActivityShipSchemaType }
  | { action: typeof OrderAction.MARK_DELIVERED; input: OrderActivityMarkDeliveredSchemaType };

function decodePayload(payload: OrderApprovalActionPayloadV1): DecodedOrderApprovalAction {
  switch (payload.action) {
    case OrderAction.SUBMIT:
      return { action: payload.action, input: OrderActionSubmitSchema.parse(payload.input) };
    case OrderAction.SHIP:
      return {
        action: payload.action,
        input: OrderActivityShipSchema.parse({
          ...payload.input,
          roOrderedAt: new Date(payload.input.roOrderedAt),
          shipBy: payload.input.shipBy === null ? null : new Date(payload.input.shipBy),
          requiredBy: payload.input.requiredBy === null ? null : new Date(payload.input.requiredBy),
          actualShipDate: new Date(payload.input.actualShipDate),
          ...(payload.input.poReceivedAt === undefined
            ? {}
            : { poReceivedAt: payload.input.poReceivedAt === null ? null : new Date(payload.input.poReceivedAt) }),
        }),
      };
    case OrderAction.MARK_DELIVERED:
      return {
        action: payload.action,
        input: OrderActivityMarkDeliveredSchema.parse({
          ...payload.input,
          roOrderedAt: new Date(payload.input.roOrderedAt),
          requiredBy: payload.input.requiredBy === null ? null : new Date(payload.input.requiredBy),
          deliveredOn: new Date(payload.input.deliveredOn),
        }),
      };
  }
}

/** Strict JSON shape first, then the complete existing owner validation, including cross-field checks. */
export const orderApprovalActionPayloadV1Schema = payloadSchema.superRefine((payload, ctx) => {
  try {
    decodePayload(payload);
  } catch (error) {
    if (!(error instanceof z.ZodError)) {
      throw error;
    }
    for (const issue of error.issues) {
      ctx.addIssue({ ...issue, path: ["input", ...issue.path] });
    }
  }
});

export function encodeOrderApprovalActionPayload(action: OrderApprovalAction, input: unknown): OrderApprovalActionPayloadV1 {
  switch (orderApprovalActionSchema.parse(action)) {
    case OrderAction.SUBMIT: {
      const parsed = OrderActionSubmitSchema.parse(input);
      return orderApprovalActionPayloadV1Schema.parse({
        version: 1,
        action,
        input: {
          ...parsed,
          items: parsed.items.map(item => ({
            ...item,
            lots: item.lots.map(lot => ({ ...lot, quantity: lot.quantity.toString() })),
          })),
        },
      });
    }
    case OrderAction.SHIP: {
      const parsed = OrderActivityShipSchema.parse(input);
      return orderApprovalActionPayloadV1Schema.parse({
        version: 1,
        action,
        input: {
          ...parsed,
          roOrderedAt: parsed.roOrderedAt.toISOString(),
          shipBy: parsed.shipBy?.toISOString() ?? null,
          requiredBy: parsed.requiredBy?.toISOString() ?? null,
          actualShipDate: parsed.actualShipDate.toISOString(),
          ...(parsed.poReceivedAt === undefined
            ? {}
            : { poReceivedAt: parsed.poReceivedAt === null ? null : parsed.poReceivedAt.toISOString() }),
        },
      });
    }
    case OrderAction.MARK_DELIVERED: {
      const parsed = OrderActivityMarkDeliveredSchema.parse(input);
      return orderApprovalActionPayloadV1Schema.parse({
        version: 1,
        action,
        input: {
          ...parsed,
          roOrderedAt: parsed.roOrderedAt.toISOString(),
          requiredBy: parsed.requiredBy?.toISOString() ?? null,
          deliveredOn: parsed.deliveredOn.toISOString(),
        },
      });
    }
  }
}

export function decodeOrderApprovalActionPayload(value: unknown): DecodedOrderApprovalAction {
  return decodePayload(orderApprovalActionPayloadV1Schema.parse(value));
}

/** The closed action/location contract; callers still validate live state and authority under owner locks. */
export function getOrderApprovalActionTransition(action: OrderApprovalAction) {
  switch (orderApprovalActionSchema.parse(action)) {
    case OrderAction.SUBMIT:
      return {
        location: effectLocationTokenSchema.parse("sales_order.status_transition.draft_to_confirmed"),
        fromStatus: OrderStatus.DRAFT,
        toStatus: OrderStatus.CONFIRMED,
      };
    case OrderAction.SHIP:
      return {
        location: effectLocationTokenSchema.parse("sales_order.status_transition.confirmed_to_shipped"),
        fromStatus: OrderStatus.CONFIRMED,
        toStatus: OrderStatus.SHIPPED,
      };
    case OrderAction.MARK_DELIVERED:
      return {
        location: effectLocationTokenSchema.parse("sales_order.status_transition.shipped_to_delivered"),
        fromStatus: OrderStatus.SHIPPED,
        toStatus: OrderStatus.DELIVERED,
      };
  }
}
